import { useTranslation } from '@repo/i18n'
import { flexRender, type Header } from '@tanstack/react-table'
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import { cn } from '#lib/utils'
import { Checkbox } from '#ui/checkbox'
import { GridHead } from '#ui/grid'
import { DataTableColumnMenu } from './column-menu'
import { getColumnPinStyles, getColumnWidthVar } from './pin-styles'
import { type DataTableColumnMenuRender, SELECT_COLUMN_ID } from './types'

export interface DataTableSelectAll {
	checked: boolean
	indeterminate: boolean
	disabled: boolean
	ariaLabel: string
	onCheckedChange: (checked: boolean) => void
}

export function DataTableHeaderCell<TData>(props: {
	header: Header<TData, unknown>
	colIndex: number
	selectAll: DataTableSelectAll | undefined
	enableColumnMenu: boolean | undefined
	renderColumnMenuItems: DataTableColumnMenuRender<TData> | undefined
	onResetColumn: (columnId: string) => void
	onResizeStart: (
		header: Header<TData, unknown>,
		event: ReactPointerEvent<HTMLElement>
	) => void
}) {
	const { t } = useTranslation()

	if (props.header.column.id === SELECT_COLUMN_ID) {
		return (
			<GridHead
				aria-colindex={props.colIndex}
				className='relative h-full shrink-0 grow-0 justify-center bg-muted'
				data-column-id={props.header.column.id}
				style={{
					width: getColumnWidthVar(props.header.column.id),
					...getColumnPinStyles(props.header.column, {
						isHeader: true
					})
				}}
			>
				{props.selectAll && (
					<Checkbox
						aria-label={props.selectAll.ariaLabel}
						checked={props.selectAll.checked}
						disabled={props.selectAll.disabled}
						indeterminate={props.selectAll.indeterminate}
						onCheckedChange={props.selectAll.onCheckedChange}
					/>
				)}
			</GridHead>
		)
	}

	const label =
		props.header.column.columnDef.meta?.label ?? props.header.column.id
	const canSort = props.header.column.getCanSort()
	const content = props.header.isPlaceholder
		? null
		: flexRender(
				props.header.column.columnDef.header,
				props.header.getContext()
			)
	const pinnedRight = props.header.column.getIsPinned() === 'right'
	const direction = props.header.column.getIsSorted() || 'none'
	let ariaSort: 'ascending' | 'descending' | 'none' | undefined
	if (canSort && direction === 'none') {
		ariaSort = 'none'
	} else if (direction === 'asc') {
		ariaSort = 'ascending'
	} else if (direction === 'desc') {
		ariaSort = 'descending'
	}

	return (
		<GridHead
			aria-colindex={props.colIndex}
			aria-sort={ariaSort}
			className='relative h-full shrink-0 grow-0 bg-muted'
			data-column-id={props.header.column.id}
			style={{
				width: getColumnWidthVar(props.header.column.id),
				...getColumnPinStyles(props.header.column, { isHeader: true })
			}}
		>
			<div
				className={cn(
					'flex h-full w-full min-w-0 items-center gap-1',
					pinnedRight && 'pl-2'
				)}
			>
				{canSort ? (
					<button
						aria-label={t('shared:data_table.sort_by', {
							column: label
						})}
						className='-ml-2 flex h-full min-w-0 flex-1 cursor-pointer items-center gap-1.5 pl-2 text-start'
						onClick={props.header.column.getToggleSortingHandler()}
						type='button'
					>
						<span className='min-w-0 truncate'>{content}</span>
						{direction === 'asc' && (
							<ChevronUp className='size-4 shrink-0' />
						)}
						{direction === 'desc' && (
							<ChevronDown className='size-4 shrink-0' />
						)}
						{direction === 'none' && (
							<ChevronsUpDown className='size-4 shrink-0 text-muted-foreground' />
						)}
					</button>
				) : (
					<span className='min-w-0 flex-1 truncate'>{content}</span>
				)}
				<DataTableColumnMenu
					column={props.header.column}
					enableColumnMenu={props.enableColumnMenu}
					label={label}
					onResetColumn={props.onResetColumn}
					renderColumnMenuItems={props.renderColumnMenuItems}
				/>
			</div>
			{props.header.column.getCanResize() && (
				<ResizeHandle
					header={props.header}
					onResetColumn={props.onResetColumn}
					onResizeStart={props.onResizeStart}
				/>
			)}
		</GridHead>
	)
}

function ResizeHandle<TData>(props: {
	header: Header<TData, unknown>
	onResetColumn: (columnId: string) => void
	onResizeStart: (
		header: Header<TData, unknown>,
		event: ReactPointerEvent<HTMLElement>
	) => void
}) {
	const pinnedRight = props.header.column.getIsPinned() === 'right'
	return (
		<div
			aria-hidden='true'
			className={cn(
				'group absolute top-0 bottom-0 flex w-2 cursor-col-resize touch-none select-none items-center justify-center hover:bg-accent active:bg-accent data-[resizing=true]:bg-accent',
				pinnedRight ? 'left-0' : 'right-0'
			)}
			onDoubleClick={() => {
				props.header.column.resetSize()
				props.onResetColumn(props.header.column.id)
			}}
			onPointerDown={(e) => {
				if (e.pointerType === 'mouse' && e.button !== 0) {
					return
				}
				props.onResizeStart(props.header, e)
			}}
			style={{ zIndex: 3 }}
		>
			<span className='h-[40%] w-0.5 bg-border group-active:h-full group-active:bg-primary group-data-[resizing=true]:h-full group-data-[resizing=true]:bg-primary' />
		</div>
	)
}
